import { useEffect, useState } from "react";
import { CurrencyDollar, Receipt, XCircle, ChartLineUp } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const RANGES = [7, 30, 90];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E2DC] p-5">
      <div className="flex items-center justify-between">
        <p className="label-caps">{label}</p>
        <Icon size={20} weight="duotone" color={accent} />
      </div>
      <p className="font-heading text-2xl font-semibold mt-3 tracking-tight">{value}</p>
    </div>
  );
}

export default function Reports() {
  const { currency } = useAuth();
  const { t, lang } = useI18n();
  const loc = lang === "es" ? "es-ES" : "en-US";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/summary", { params: { days } }).then(({ data }) => setData(data)).catch(() => {});
  }, [days]);

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /></div>;

  const daily = data.daily_revenue.map((d) => ({ ...d, label: new Date(d.date + "T00:00:00").toLocaleDateString(loc, { month: "short", day: "numeric" }) }));

  return (
    <div className="fade-up space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.reports.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.reports.title}</h1>
        </div>
        <div className="flex items-center gap-1 bg-white border border-[#E5E2DC] rounded-lg p-1">
          {RANGES.map((d) => (
            <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${days === d ? "bg-[#2C3E35] text-white" : "text-[#6B6A67] hover:bg-[#E8E4DF]"}`}>{t.reports[`r${d}`]}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={CurrencyDollar} label={t.reports.revenue} value={money(data.total_revenue, currency)} accent="#2C3E35" />
        <Stat icon={Receipt} label={t.reports.avgTicket} value={money(data.avg_ticket, currency)} accent="#7A8B5C" />
        <Stat icon={ChartLineUp} label={t.reports.completed} value={data.completed} accent="#B86B5A" />
        <Stat icon={XCircle} label={t.reports.cancelledNo} value={data.cancelled + data.no_show} accent="#C9A227" />
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
        <p className="label-caps mb-5">{t.reports.trend}</p>
        {daily.length === 0 ? <p className="text-sm text-[#6B6A67]">{t.reports.noPeriod}</p> : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={daily} margin={{ left: -16, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" vertical={false} />
              <XAxis dataKey="label" stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} formatter={(v) => money(v, currency)} />
              <Line type="monotone" dataKey="revenue" stroke="#2C3E35" strokeWidth={2.5} dot={{ r: 3, fill: "#2C3E35" }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
          <p className="label-caps mb-5">{t.reports.byStaff}</p>
          {data.staff_revenue.length === 0 ? <p className="text-sm text-[#6B6A67]">{t.reports.noData}</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.staff_revenue} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={100} stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "#E8E4DF55" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} formatter={(v) => money(v, currency)} />
                <Bar dataKey="revenue" radius={[0, 6, 6, 0]} fill="#2C3E35" barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
          <p className="label-caps mb-5">{t.reports.byService}</p>
          {data.service_revenue.length === 0 ? <p className="text-sm text-[#6B6A67]">{t.reports.noData}</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.service_revenue} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={100} stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "#E8E4DF55" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} formatter={(v) => money(v, currency)} />
                <Bar dataKey="revenue" radius={[0, 6, 6, 0]} fill="#B86B5A" barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
